export class LocalDate {
  static get None() {
    return new LocalDate(null, null, null);
  }

  static fromDate(date: any): LocalDate {
    if (date === null || date === undefined) return new LocalDate(null, null, null);
    if (date instanceof Date) return new LocalDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
    if (date instanceof LocalDate) return date.clone();
    if (typeof date === 'string') return LocalDate.fromDate(new Date(date));
    if (date._isAMomentObject) return new LocalDate(date.year(), date.month() + 1, date.day());
    if (date.year && date.month && date.day) return new LocalDate(date.year, date.month, date.day);
  }

  static today(): LocalDate {
    return LocalDate.fromDate(new Date());
  }

  constructor(year: number, month: number, day: number) {
    this.year = year;
    this.month = month;
    this.day = day;
  }

  public hasValue(): boolean {
    return this.year !== null && !Number.isNaN(this.year) && this.year !== undefined;
  }

  public year: number;
  public month: number;
  public day: number;

  setDate(date: Date) {
    if (date == null) {
      this.year = null;
      this.month = null;
      this.day = null;
    } else {
      this.year = date.getFullYear();
      this.month = date.getMonth() + 1;
      this.day = date.getDate();
    }
  }

  toDate(): Date {
    if (this.year === null) return null;
    return new Date(this.year, this.month - 1, this.day);
  }

  toJSON() {
    return { year: this.year, month: this.month, day: this.day };
  }

  toLocaleDateString(culture?: string) {
    if (!this.hasValue()) return '';
    return this.toDate().toLocaleDateString(culture);
  }

  public addYears(years: number): Date {
    if (!this.hasValue()) return null;

    this.year = Math.max(this.year + years, 0);
    this.day = Math.min(this.day, LocalDate.daysInMonth(this.year, this.month));

    return this.toDate();
  }

  public addMonths(months: number): Date {
    if (!this.hasValue()) return null;

    if (months > 0) {
      var monthToAdd = this.month + months;

      var yearsToAdd = 0;
      while (monthToAdd > 12) {
        yearsToAdd++;
        monthToAdd -= 12;
      }

      this.month = monthToAdd;
      this.year = this.year + yearsToAdd;
      this.day = Math.min(this.day, LocalDate.daysInMonth(this.year, this.month));
    } else {
      months = Math.abs(months);
      var monthsToRemove = months % 12;
      var yearsToRemove = (months - monthsToRemove) / 12;

      var resultMonth = this.month - monthsToRemove;
      if (resultMonth <= 0) {
        yearsToRemove++;
        resultMonth = 12 + resultMonth;
      }

      this.month = resultMonth;
      this.year = this.year - yearsToRemove;
      this.day = Math.min(this.day, LocalDate.daysInMonth(this.year, this.month));
    }

    return this.toDate();
  }

  public addDays(days: number): Date {
    var direction = days < 0 ? -1 : 1;
    for (var i = 0; i < days * direction; i++) {
      var resultDay = this.day + direction;
      if (resultDay > LocalDate.daysInMonth(this.year, this.month)) {
        this.addMonths(1);
        resultDay = 1;
      } else if (resultDay < 1) {
        this.addMonths(-1);
        resultDay = LocalDate.daysInMonth(this.year, this.month);
      }
      this.day = resultDay;
    }
    return this.toDate();
  }

  public static daysInMonth(year: number, month: number): number {
    let monthsWith31Days = [1, 3, 5, 7, 8, 10, 12];
    if (monthsWith31Days.includes(month)) return 31;
    if (month == 2) return LocalDate.isLeapYear(year) ? 29 : 28;
    return 30;
  }

  public clone(): LocalDate {
    var result = new LocalDate(null, null, null);
    if (this.hasValue()) result.copyFrom(this);
    return result;
  }

  public copyFrom(date: LocalDate) {
    this.setDate(date.toDate());
  }

  public lastDayInMonth(): Date {
    var date = this.toDate();
    date.setDate(LocalDate.daysInMonth(this.year, this.month));
    return date;
  }

  public firstDayInMonth(): Date {
    var date = this.toDate();
    date.setDate(1);
    return date;
  }

  public static Equal(date1: LocalDate | Date, date2: LocalDate | Date): boolean {
    var ld1 = LocalDate.fromDate(date1);
    var ld2 = LocalDate.fromDate(date2);
    if (!ld1.hasValue() && !ld2.hasValue()) return true;
    if (!ld1.hasValue() || !ld2.hasValue()) return false;
    return ld1.toDate().getTime() === ld2.toDate().getTime();
  }

  public static GreaterThan(date1: LocalDate | Date, date2: LocalDate | Date): boolean {
    var ld1 = LocalDate.fromDate(date1);
    var ld2 = LocalDate.fromDate(date2);
    if (!ld1.hasValue() || !ld2.hasValue()) return false;
    return ld1.toDate().getTime() > ld2.toDate().getTime();
  }

  public static isLeapYear(year: number): boolean {
    if (year == null) return null;
    if (year % 400 == 0) return true;
    if (year % 100 == 0) return false;
    return year % 4 == 0;
  }

  public toISOString(): string {
    if (!this.hasValue()) return '';
    return (
      this.year.toString(10).padStart(4, '0') +
      '-' +
      this.month.toString(10).padStart(2, '0') +
      '-' +
      this.day.toString(10).padStart(2, '0')
    );
  }

  public toString(): string {
    return this.toISOString();
  }

  public isLeapYear(): boolean {
    return LocalDate.isLeapYear(this.year);
  }

  public static isLocalDate(date: any): date is LocalDate {
    return date instanceof LocalDate;
  }

  valueOf(): number {
    return this.hasValue() ? this.toDate().valueOf() : Number.NaN; // <-- achive correct results when using comparison operators (>,>=,<,<=) with "empty" LocalDates
  }
  [Symbol.toPrimitive](hint: string): string | number {
    if (hint === 'number') {
      return this.valueOf();
    }
    return this.toISOString();
  }
}
